import type {
  AlertPresetBase,
  AlertPresetOutcome,
  AlertType,
  Density,
  InsightAlert,
  InsightAlertOutcome,
} from './variables.typing'
import type { SpaceType } from './spaceTime.types'

type AlertLike = AlertType | InsightAlert | null | undefined
type AlertDensityContext = { betterDensity?: Density | null } | null | undefined

type AlertPresetConfig = {
  label: string
  direction?: 'increase' | 'decrease'
  defaultOutcome: AlertPresetOutcome
  relevance: number
  goodColor: string
  badColor: string
  goodTextColor?: string
  badTextColor?: string
}

type AlertPresetMetadata = {
  base: AlertPresetBase
  outcome: AlertPresetOutcome
  label: string
  color: string
  textColor: string
  relevance: number
}

export const spaceTypeSingularLabels: Record<SpaceType, string> = {
  item: 'Item',
  community: 'Comunidade',
  neighborhood: 'Bairro',
  district: 'Distrito',
  street: 'Rua',
  municipality: 'Município',
  microRegion: 'Microrregião',
  mesoRegion: 'Mesorregião',
  state: 'Estado',
  macroRegion: 'Macrorregião',
  country: 'País',
  custom: 'Personalizado',
}

export const spaceTypeGrammaticalGender = {
  item: 'male',
  community: 'female',
  neighborhood: 'male',
  district: 'male',
  street: 'female',
  municipality: 'male',
  microRegion: 'female',
  mesoRegion: 'female',
  state: 'male',
  macroRegion: 'female',
  country: 'male',
  custom: 'male',
} as const

export const compatibleSpaceChildren: Record<SpaceType, SpaceType[]> = {
  custom: ['item'],
  country: ['state', 'macroRegion', 'mesoRegion', 'microRegion', 'item', 'country'],
  macroRegion: ['state', 'mesoRegion', 'microRegion', 'item', 'macroRegion'],
  state: ['municipality', 'mesoRegion', 'microRegion', 'item', 'state'],
  mesoRegion: ['municipality', 'microRegion', 'item', 'mesoRegion'],
  microRegion: ['municipality', 'neighborhood', 'item', 'microRegion'],
  municipality: ['district', 'neighborhood', 'street', 'community', 'item', 'municipality'],
  community: ['item', 'community'],
  district: ['neighborhood', 'street', 'item', 'district'],
  neighborhood: ['street', 'item', 'neighborhood'],
  street: ['item', 'street'],
  item: [],
}

export const spaceTypePluralLabels: Record<SpaceType, string> = {
  item: 'Itens',
  community: 'Comunidades',
  neighborhood: 'Bairros',
  district: 'Distritos',
  street: 'Ruas',
  municipality: 'Municípios',
  microRegion: 'Microrregiões',
  mesoRegion: 'Mesorregiões',
  state: 'Estados',
  macroRegion: 'Macrorregiões',
  country: 'Países',
  custom: 'Personalizado',
}

export const childrenItemViewLabels = {
  heatmap: 'Mapa de calor',
  cluster: 'Agrupamentos',
  arc: 'Arcos',
}

export const ALERT_PRESET_BASES = [
  'notification',
  'outbreak',
  'urgent',
  'anomaly',
  'emergent',
  'new',
  'decline',
  'important',
  'informative',
  'normal',
  'increase',
  'decrease',
  'sparse',
] as const satisfies readonly AlertPresetBase[]

export const ALERT_PRESET_OUTCOMES = [
  'good',
  'bad',
] as const satisfies readonly AlertPresetOutcome[]

const alertPresetConfig: Record<AlertPresetBase, AlertPresetConfig> = {
  notification: {
    label: 'Agravo',
    defaultOutcome: 'bad',
    relevance: 0.9,
    goodColor: '#0F766E',
    badColor: '#000000',
  },
  outbreak: {
    label: 'Anomalia',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.6,
    goodColor: '#0891B2',
    badColor: '#5f27cd',
  },
  urgent: {
    label: 'Urgente',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.4,
    goodColor: '#15803D',
    badColor: '#c42210',
  },
  anomaly: {
    label: 'Anomalia',
    defaultOutcome: 'bad',
    relevance: 0.5,
    goodColor: '#0284C7',
    badColor: '#5f27cd',
  },
  emergent: {
    label: 'Emergente',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.2,
    goodColor: '#16A34A',
    badColor: '#0dc022',
  },
  new: {
    label: 'Novo',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.2,
    goodColor: '#0F766E',
    badColor: '#16a085',
  },
  decline: {
    label: 'Declínio',
    direction: 'decrease',
    defaultOutcome: 'bad',
    relevance: 0.1,
    goodColor: '#0F766E',
    badColor: '#c42210',
  },
  important: {
    label: 'Importante',
    defaultOutcome: 'bad',
    relevance: 0.3,
    goodColor: '#2563EB',
    badColor: '#FF9800',
  },
  informative: {
    label: 'Informações',
    defaultOutcome: 'good',
    relevance: 0.05,
    goodColor: '#0284C7',
    badColor: '#00BCD4',
  },
  normal: {
    label: 'Normal',
    defaultOutcome: 'good',
    relevance: 0.01,
    goodColor: '#E5E7EB',
    badColor: '#D1D5DB',
    goodTextColor: '#374151',
    badTextColor: '#374151',
  },
  increase: {
    label: 'Aumento',
    direction: 'increase',
    defaultOutcome: 'bad',
    relevance: 0.08,
    goodColor: '#16A34A',
    badColor: '#e74c3c',
  },
  decrease: {
    label: 'Diminuição',
    direction: 'decrease',
    defaultOutcome: 'bad',
    relevance: 0.08,
    goodColor: '#0F766E',
    badColor: '#EF4444',
  },
  sparse: {
    label: 'Poucos dados',
    defaultOutcome: 'bad',
    relevance: 0.08,
    goodColor: '#0F766E',
    badColor: '#7f8c8d',
  },
}

function capitalizeAlertBase(base: AlertPresetBase): Capitalize<AlertPresetBase> {
  return `${base.charAt(0).toUpperCase()}${base.slice(1)}` as Capitalize<AlertPresetBase>
}

export function toAlertType(outcome: AlertPresetOutcome, base: AlertPresetBase): AlertType {
  return `${outcome}${capitalizeAlertBase(base)}` as AlertType
}

const alertPresetMetadata = Object.fromEntries(
  ALERT_PRESET_BASES.flatMap(base =>
    ALERT_PRESET_OUTCOMES.map(outcome => {
      const config = alertPresetConfig[base]
      return [
        toAlertType(outcome, base),
        {
          base,
          outcome,
          label: config.label,
          color: outcome === 'good' ? config.goodColor : config.badColor,
          textColor:
            outcome === 'good'
              ? (config.goodTextColor ?? '#FFFFFF')
              : (config.badTextColor ?? '#FFFFFF'),
          relevance: config.relevance,
        } satisfies AlertPresetMetadata,
      ]
    }),
  ),
) as Record<AlertType, AlertPresetMetadata>

export const alertLabels = Object.fromEntries(
  Object.entries(alertPresetMetadata).map(([alertType, metadata]) => [alertType, metadata.label]),
) as Record<AlertType, string>

export const alertColors = Object.fromEntries(
  Object.entries(alertPresetMetadata).map(([alertType, metadata]) => [alertType, metadata.color]),
) as Record<AlertType, string>

export const alertTextColors = Object.fromEntries(
  Object.entries(alertPresetMetadata).map(([alertType, metadata]) => [
    alertType,
    metadata.textColor,
  ]),
) as Record<AlertType, string>

export const alertRelevance = Object.fromEntries(
  Object.entries(alertPresetMetadata).map(([alertType, metadata]) => [
    alertType,
    metadata.relevance,
  ]),
) as Record<AlertType, number>

export function isAlertType(value: unknown): value is AlertType {
  return typeof value === 'string' && value in alertPresetMetadata
}

export function getAlertBaseFromType(alertType: AlertType): AlertPresetBase {
  return alertPresetMetadata[alertType].base
}

export function getAlertOutcomeFromType(alertType: AlertType): AlertPresetOutcome {
  return alertPresetMetadata[alertType].outcome
}

export function resolveAlertOutcomeForVariable(
  base: AlertPresetBase,
  variable?: AlertDensityContext,
): AlertPresetOutcome {
  const config = alertPresetConfig[base]

  if (config.direction === 'increase') {
    if (variable?.betterDensity === 'more') return 'good'
    if (variable?.betterDensity === 'less') return 'bad'
  }

  if (config.direction === 'decrease') {
    if (variable?.betterDensity === 'less') return 'good'
    if (variable?.betterDensity === 'more') return 'bad'
  }

  return config.defaultOutcome
}

export function resolveAlertTypeForVariable(
  base: AlertPresetBase,
  variable?: AlertDensityContext,
  outcome?: AlertPresetOutcome,
): AlertType {
  return toAlertType(outcome ?? resolveAlertOutcomeForVariable(base, variable), base)
}

export function getAlertOutcomeFromAlert(
  alert: AlertLike,
  fallback?: InsightAlertOutcome,
): InsightAlertOutcome {
  if (fallback === 'normal' || fallback === 'good' || fallback === 'bad') {
    return fallback
  }

  if (!alert) {
    return 'normal'
  }

  if (typeof alert === 'string' && isAlertType(alert)) {
    return getAlertOutcomeFromType(alert)
  }

  if (typeof alert === 'object' && 'preset' in alert && isAlertType(alert.preset)) {
    return getAlertOutcomeFromType(alert.preset)
  }

  return fallback ?? 'bad'
}

function isPresetAlert(alert: AlertLike): alert is Extract<InsightAlert, { preset: AlertType }> {
  return alert !== null && alert !== undefined && typeof alert === 'object' && 'preset' in alert
}

function isCustomAlert(alert: AlertLike): alert is Extract<InsightAlert, { color: string }> {
  return (
    alert !== null &&
    alert !== undefined &&
    typeof alert === 'object' &&
    !('preset' in alert) &&
    'color' in alert
  )
}

function resolvePreset(alert: AlertLike): AlertType | null {
  if (!alert) {
    return null
  }

  if (typeof alert === 'string') {
    return isAlertType(alert) ? alert : null
  }

  if (isPresetAlert(alert)) {
    return isAlertType(alert.preset) ? alert.preset : null
  }

  return null
}

export function getAlertLabel(alert: AlertLike): string {
  if (!alert) {
    return ''
  }

  if (isCustomAlert(alert)) {
    return alert.name
  }

  if (isPresetAlert(alert)) {
    return alert.name ?? (isAlertType(alert.preset) ? alertLabels[alert.preset] : alert.preset)
  }

  return isAlertType(alert) ? alertLabels[alert] : alert
}

export function getAlertColor(alert: AlertLike): string {
  if (isCustomAlert(alert)) {
    return alert.color
  }

  const preset = resolvePreset(alert)
  return (preset && alertColors[preset]) || alertColors.goodNormal
}

export function getAlertTextColor(alert: AlertLike): string {
  if (isCustomAlert(alert)) {
    return '#FFFFFF'
  }

  const preset = resolvePreset(alert)
  return (preset && alertTextColors[preset]) || '#FFFFFF'
}
